import ColorsStyles from '@Styles/colors.scss';
import { useAuth0 } from '@auth0/auth0-react';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import FolderStyles from '@Components/FolderComponents/Folders.scss';
import PopoverMenu from '@Components/PopoverMenu/PopoverMenu';
import PopoverMenuItem from '@Components/PopoverMenu/PopoverMenuItem';
import { selectedFolderState } from '@Data/folders/Reducer';
import ProcessesApi from '@Data/processes/Api';
import { tryCreateProcessNotificationForTask } from '@Data/processes/Helpers';
import { IRunningProcess } from '@Data/processes/Interfaces';
import { linkDepartmentProcessState } from '@Data/processes/Reducer';
import { TaskType } from '@Data/processes/TaskType';
import ProjectsApi from '@Data/projects/Api';
import { selectedProjectState } from '@Data/projects/Reducer';
import { selectedTeamState } from '@Data/teams/Reducer';
import { getParsedAuth0Username } from '@Services/authentication/Helpers';

const ProjectFolderMenu = (props) => {
    const {
        id,
        folder,
        onEdit,
        onProcess,
        onShare,
        onZoomToBounds,
        isVisible,
        onCreateSubset,
        isSelected,
        onClose,
        onViewFiles,
        onOpenJobSettings,
    } = props;

    const menuRef = useRef<PopoverMenu>();
    const [btnColour, setBtnColour] = useState('#000');
    const selectedProject = useSelector(selectedProjectState);
    const selectedTeam = useSelector(selectedTeamState);
    const linkDepartmentProcess = useSelector(linkDepartmentProcessState);
    const { user } = useAuth0();
    let syncDeltaString = '';
    const lastIoSyncTime: string = folder?.last_io_sync_time;
    if (!_isNil(lastIoSyncTime)) {
        const syncDelta = Date.now() - Date.parse(lastIoSyncTime);
        syncDeltaString = `${Math.floor(syncDelta / 60000)}m ago`;
    }

    const handleSetColours = (isOpen: boolean) => {
        if (isSelected) {
            setBtnColour(isOpen ? '#000' : '#fff');
        } else {
            setBtnColour(
                isVisible
                    ? ColorsStyles.ikeGradient1
                    : ColorsStyles.ikeGradient8
            );
        }
    };

    const handleClose = () => {
        handleSetColours(false);
        if (!_isNil(onClose)) {
            onClose();
        }
    };

    const handleFolderSync = async () => {
        const processSchema = {
            departmentId: selectedProject?.department_id,
            departmentName: selectedProject?.department_name,
            jobId: folder?.job_id,
        };

        await ProcessesApi.runProcess(
            linkDepartmentProcess,
            processSchema,
            selectedProject.project_id,
            getParsedAuth0Username(user.sub),
            (runningProcess: IRunningProcess) => {
                tryCreateProcessNotificationForTask(
                    runningProcess.process_name,
                    runningProcess.task_id,
                    TaskType.SYNC
                );
            }
        );
        await ProjectsApi.getAndSelectProjectById(
            selectedTeam.team_id,
            selectedProject.project_id
        );
        ProcessesApi.setProcess(null);
        handleClose();
    };

    useEffect(() => {
        if (isSelected) {
            setBtnColour('#fff');
        } else {
            setBtnColour(
                isVisible
                    ? ColorsStyles.ikeGradient1
                    : ColorsStyles.ikeGradient8
            );
        }
    }, [isSelected, isVisible]);

    return (
        <div id={`${id}-container`} className={FolderStyles.foMenuContainer}>
            <PopoverMenu
                id={id}
                ref={menuRef}
                iconColour={btnColour}
                onClose={handleClose}
                onOpen={() => handleSetColours(true)}
            >
                {isVisible && (
                    <PopoverMenuItem
                        id={`${id}-zoom`}
                        onClick={onZoomToBounds}
                        text="zoom"
                        parentRef={menuRef}
                    />
                )}
                {folder?.folder_type === 'io_import_v2' && (
                    <PopoverMenuItem
                        id={`${id}-sync`}
                        onClick={() => handleFolderSync()}
                        text={`Sync folder ${syncDeltaString}`}
                        parentRef={menuRef}
                    />
                )}
                <PopoverMenuItem
                    id={`${id}-rename`}
                    onClick={onEdit}
                    text="rename"
                    parentRef={menuRef}
                />
                <PopoverMenuItem
                    id={`${id}-process`}
                    onClick={onProcess}
                    text="process"
                    parentRef={menuRef}
                />
                <PopoverMenuItem
                    id={`${id}-share`}
                    onClick={onShare}
                    text="share"
                    parentRef={menuRef}
                />
                <PopoverMenuItem
                    id={`${id}-create-subset`}
                    onClick={onCreateSubset}
                    text="create subset"
                    parentRef={menuRef}
                />
                <PopoverMenuItem
                    id={`${id}-files`}
                    onClick={onViewFiles}
                    text="files"
                    parentRef={menuRef}
                />
                <PopoverMenuItem
                    id={`${id}-jobSettings`}
                    onClick={onOpenJobSettings}
                    text="job settings"
                    parentRef={menuRef}
                />
            </PopoverMenu>
        </div>
    );
};

ProjectFolderMenu.propTypes = {
    id: PropTypes.string,
    folder: PropTypes.object,
    onEdit: PropTypes.func,
    onProcess: PropTypes.func,
    onShare: PropTypes.func,
    onZoomToBounds: PropTypes.func,
    isVisible: PropTypes.bool,
    isSelected: PropTypes.bool,
    onClose: PropTypes.func,
    onCreateSubset: PropTypes.func,
    isHovering: PropTypes.bool,
    onViewFiles: PropTypes.func,
    onOpenJobSettings: PropTypes.func,
};
export default ProjectFolderMenu;
